import React from "react";
import { CheckCircle2, PawPrint, AlertCircle } from "lucide-react";
import PublicLayout from "../components/PublicLayout";
import { api, formatApiErrorDetail } from "../lib/api";

const TEST_INTERESTS = [
  "Account signup/login",
  "Dog profiles",
  "Dog matching questionnaire",
  "Match grades",
  "Buddy requests",
  "Messaging",
  "Meetups",
  "Dog parks",
  "Open in Maps",
  "Safety/report/block tools",
];

const DOG_SIZES = ["small", "medium", "large", "extra large", "multiple"];

const emptyForm = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  city: "",
  state: "",
  platform: "",
  device_model: "",
  os_version: "",
  owns_dog: "",
  dog_count: "",
  dog_size: "",
  test_interests: [],
  consent_to_contact: false,
  accepted_privacy_terms: false,
};

function Field({ label, required, children, testId, help }) {
  return (
    <label className="block" data-testid={`field-${testId}`}>
      <div className="text-[13px] font-bold text-bark-ink mb-1.5">
        {label}
        {required && <span className="text-bark-error ml-0.5">*</span>}
      </div>
      {children}
      {help && <div className="mt-1 text-[12px] text-bark-muted">{help}</div>}
    </label>
  );
}

const inputCls =
  "w-full rounded-2xl bg-white border border-transparent shadow-bark-soft px-4 py-3 text-[15px] text-bark-ink placeholder:text-bark-muted focus:border-bark-teal focus:outline-none transition-colors duration-150";

const selectCls = inputCls + " appearance-none";

export default function BetaPage() {
  const [form, setForm] = React.useState(emptyForm);
  const [submitting, setSubmitting] = React.useState(false);
  const [success, setSuccess] = React.useState(false);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    document.title = "Join the Android Beta | Bark Buddies";
  }, []);

  const set = (k) => (e) => {
    const val = e && e.target ? e.target.value : e;
    setForm((f) => ({ ...f, [k]: val }));
  };
  const setBool = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.checked }));
  };
  const toggleInterest = (val) => {
    setForm((f) => ({
      ...f,
      test_interests: f.test_interests.includes(val)
        ? f.test_interests.filter((x) => x !== val)
        : [...f.test_interests, val],
    }));
  };

  const validate = () => {
    const required = [
      "first_name",
      "last_name",
      "email",
      "city",
      "state",
      "platform",
      "device_model",
      "os_version",
    ];
    for (const k of required) if (!String(form[k] || "").trim()) return "Please fill in all required fields.";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) return "Please enter a valid email address.";
    if (form.owns_dog === "") return "Please tell us if you own a dog.";
    if (!form.consent_to_contact) return "Please agree to receive beta testing emails.";
    if (!form.accepted_privacy_terms) return "Please accept the Privacy Policy and Terms of Service.";
    return "";
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const v = validate();
    if (v) {
      setError(v);
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        owns_dog: form.owns_dog === "yes",
        dog_count: form.dog_count === "" ? 0 : Number(form.dog_count),
      };
      await api.post("/beta-signups", payload);
      setSuccess(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <PublicLayout>
        <section className="mx-auto max-w-2xl px-5 md:px-8 py-20" data-testid="beta-success">
          <div className="bg-white rounded-[28px] shadow-bark-soft p-8 md:p-12 text-center">
            <div className="w-16 h-16 mx-auto rounded-full bg-bark-teal-soft grid place-items-center mb-4">
              <CheckCircle2 size={32} className="text-bark-teal" />
            </div>
            <h1 className="text-2xl md:text-3xl font-black tracking-tight text-bark-ink">
              Thank you for signing up for the Bark Buddies beta.
            </h1>
            <p className="mt-3 text-[15px] text-bark-ink2 leading-relaxed">
              If selected, you will receive testing instructions by email.
            </p>
            <p className="mt-6 text-[13px] text-bark-muted">
              Questions? Reach us at{" "}
              <a className="bark-link" href="mailto:support@barkbuddies.app">
                support@barkbuddies.app
              </a>
            </p>
          </div>
        </section>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <section className="mx-auto max-w-3xl px-5 md:px-8 pt-12 md:pt-16 pb-8" data-testid="beta-header">
        <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur px-4 py-1.5 rounded-full text-[12px] font-bold uppercase tracking-widest text-bark-teal shadow-bark-soft">
          <PawPrint size={14} /> Beta Signup
        </div>
        <h1 className="mt-4 text-4xl sm:text-5xl font-black tracking-tight text-bark-ink leading-[1.05]">
          Join the Android Bark Buddies Beta
        </h1>
        <p className="mt-4 text-[16px] text-bark-ink2 leading-relaxed">
          Bark Buddies is inviting early testers to help improve the dog
          playdate app before public launch. Beta testers may be asked to test
          signup, dog profiles, matching, buddy requests, messaging, meetups,
          dog park search, Open in Maps, safety tools, and app stability.
        </p>
      </section>

      <form
        onSubmit={submit}
        className="mx-auto max-w-3xl px-5 md:px-8 pb-24"
        data-testid="beta-form"
        noValidate
      >
        <div className="bg-white rounded-[28px] shadow-bark-soft p-6 md:p-9 space-y-8">
          {/* About you */}
          <div>
            <h2 className="text-[13px] font-bold uppercase tracking-widest text-bark-teal mb-4">
              About you
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="First name" required testId="first-name">
                <input
                  className={inputCls}
                  value={form.first_name}
                  onChange={set("first_name")}
                  data-testid="input-first-name"
                  autoComplete="given-name"
                />
              </Field>
              <Field label="Last name" required testId="last-name">
                <input
                  className={inputCls}
                  value={form.last_name}
                  onChange={set("last_name")}
                  data-testid="input-last-name"
                  autoComplete="family-name"
                />
              </Field>
              <Field label="Email address" required testId="email">
                <input
                  type="email"
                  className={inputCls}
                  value={form.email}
                  onChange={set("email")}
                  data-testid="input-email"
                  autoComplete="email"
                />
              </Field>
              <Field label="Phone number" testId="phone" help="Optional">
                <input
                  className={inputCls}
                  value={form.phone}
                  onChange={set("phone")}
                  data-testid="input-phone"
                  autoComplete="tel"
                />
              </Field>
              <Field label="City" required testId="city">
                <input
                  className={inputCls}
                  value={form.city}
                  onChange={set("city")}
                  data-testid="input-city"
                />
              </Field>
              <Field label="State" required testId="state">
                <input
                  className={inputCls}
                  value={form.state}
                  onChange={set("state")}
                  data-testid="input-state"
                />
              </Field>
            </div>
          </div>

          {/* Device */}
          <div>
            <h2 className="text-[13px] font-bold uppercase tracking-widest text-bark-teal mb-4">
              Your device
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Platform" required testId="platform">
                <select
                  className={selectCls}
                  value={form.platform}
                  onChange={set("platform")}
                  data-testid="select-platform"
                >
                  <option value="">Select platform</option>
                  <option value="iOS">iOS</option>
                  <option value="Android">Android</option>
                  <option value="Both">Both</option>
                </select>
              </Field>
              <Field label="Device model" required testId="device-model" help="e.g. iPhone 15 Pro, Pixel 8">
                <input
                  className={inputCls}
                  value={form.device_model}
                  onChange={set("device_model")}
                  data-testid="input-device-model"
                />
              </Field>
              <Field label="OS version" required testId="os-version" help="e.g. iOS 17.5, Android 14">
                <input
                  className={inputCls}
                  value={form.os_version}
                  onChange={set("os_version")}
                  data-testid="input-os-version"
                />
              </Field>
            </div>
          </div>

          {/* Your dogs */}
          <div>
            <h2 className="text-[13px] font-bold uppercase tracking-widest text-bark-teal mb-4">
              Your dogs
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Do you own a dog?" required testId="owns-dog">
                <select
                  className={selectCls}
                  value={form.owns_dog}
                  onChange={set("owns_dog")}
                  data-testid="select-owns-dog"
                >
                  <option value="">Select</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </Field>
              <Field label="How many dogs?" testId="dog-count">
                <input
                  type="number"
                  min="0"
                  className={inputCls}
                  value={form.dog_count}
                  onChange={set("dog_count")}
                  data-testid="input-dog-count"
                />
              </Field>
              <Field label="Dog size" testId="dog-size">
                <select
                  className={selectCls}
                  value={form.dog_size}
                  onChange={set("dog_size")}
                  data-testid="select-dog-size"
                >
                  <option value="">Select size</option>
                  {DOG_SIZES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
          </div>

          {/* Interests */}
          <div>
            <h2 className="text-[13px] font-bold uppercase tracking-widest text-bark-teal mb-2">
              What features are you interested in testing?
            </h2>
            <p className="text-[13px] text-bark-muted mb-4">Select all that apply.</p>
            <div className="grid sm:grid-cols-2 gap-2">
              {TEST_INTERESTS.map((val) => {
                const checked = form.test_interests.includes(val);
                return (
                  <label
                    key={val}
                    className={`flex items-start gap-3 p-3 rounded-2xl border cursor-pointer transition-colors duration-150 ${
                      checked
                        ? "bg-bark-teal-soft border-bark-teal"
                        : "bg-white border-transparent hover:border-bark-teal/40"
                    }`}
                    data-testid={`interest-${val.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  >
                    <input
                      type="checkbox"
                      className="mt-0.5 h-4 w-4 accent-bark-teal"
                      checked={checked}
                      onChange={() => toggleInterest(val)}
                    />
                    <span className="text-[14px] font-semibold text-bark-ink">{val}</span>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Consent */}
          <div className="space-y-3">
            <label
              className="flex items-start gap-3 p-4 rounded-2xl bg-bark-bg/60 cursor-pointer"
              data-testid="consent-block-contact"
            >
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 accent-bark-teal"
                checked={form.consent_to_contact}
                onChange={setBool("consent_to_contact")}
                data-testid="checkbox-consent-contact"
              />
              <span className="text-[14px] text-bark-ink leading-relaxed">
                I agree to receive Bark Buddies beta testing emails.
              </span>
            </label>
            <label
              className="flex items-start gap-3 p-4 rounded-2xl bg-bark-bg/60 cursor-pointer"
              data-testid="consent-block-legal"
            >
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 accent-bark-teal"
                checked={form.accepted_privacy_terms}
                onChange={setBool("accepted_privacy_terms")}
                data-testid="checkbox-accepted-terms"
              />
              <span className="text-[14px] text-bark-ink leading-relaxed">
                I agree to the Bark Buddies{" "}
                <a className="bark-link" href="/privacy" target="_blank" rel="noreferrer">
                  Privacy Policy
                </a>{" "}
                and{" "}
                <a className="bark-link" href="/terms" target="_blank" rel="noreferrer">
                  Terms of Service
                </a>
                .
              </span>
            </label>
          </div>

          {error && (
            <div
              className="flex items-start gap-2 p-4 rounded-2xl bg-red-50 border border-red-200 text-red-700 text-[14px]"
              data-testid="beta-error"
            >
              <AlertCircle size={18} className="mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div className="pt-2">
            <button
              type="submit"
              disabled={submitting}
              className="w-full rounded-full bg-bark-teal px-7 py-4 text-white font-extrabold text-[15px] shadow-bark-soft hover:bg-bark-teal-hover hover:-translate-y-0.5 transition-transform duration-200 disabled:opacity-70 disabled:cursor-not-allowed"
              data-testid="beta-submit"
            >
              {submitting ? "Submitting…" : "Submit Beta Signup"}
            </button>
            <p className="mt-3 text-center text-[12px] text-bark-muted">
              You can update your submission by using the same email address.
            </p>
          </div>
        </div>
      </form>
    </PublicLayout>
  );
}
